import React, { useState, useEffect, useCallback, useRef } from 'react';
import './OfferPopup.css';
import posterAsset from '../../../assets/images/grand-inauguration-offer.jpg';

const PUBLIC_POSTER_FALLBACK = `${process.env.PUBLIC_URL || ''}/assets/images/grand-inauguration-offer.jpg`;

// Grand Inauguration Target Date: 06 November 2026, 11:00 AM IST (UTC+05:30)
const INAUGURATION_TIMESTAMP = new Date('2026-11-06T11:00:00+05:30').getTime();

// Direct universal Google Maps search URL (compatible with all desktop browsers, Android, and iOS)
const GOOGLE_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Tiwari Syndicate Private Limited, Near Majhaulia Chowk, Majhaulia, Muzaffarpur, Bihar 842001');

const calculateTimeLeft = () => {
  const difference = INAUGURATION_TIMESTAMP - Date.now();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isComplete: true };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    isComplete: false,
  };
};

const padZero = (value) => String(value).padStart(2, '0');

export default function OfferPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [imgSrc, setImgSrc] = useState(posterAsset || PUBLIC_POSTER_FALLBACK);
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);
  const closingTimeoutRef = useRef(null);

  // Live countdown timer ticking every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Automatic delay after website loads (650ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 650);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);

    closingTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 220);
  }, [isClosing]);

  // Clean up closing timeout on unmount
  useEffect(() => {
    return () => {
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
      }
    };
  }, []);

  // Background scroll locking and Escape key handling
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Compensate scrollbar width to prevent page horizontal shifting
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow || '';
      document.body.style.paddingRight = originalPaddingRight || '';
    };
  }, [isOpen, handleClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleImageError = () => {
    if (imgSrc !== PUBLIC_POSTER_FALLBACK) {
      setImgSrc(PUBLIC_POSTER_FALLBACK);
    }
  };

  // Reliable handler to open Google Maps in all browsers without being blocked
  const handleReachUs = (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const newTab = window.open(GOOGLE_MAPS_URL, '_blank', 'noopener,noreferrer');
      if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
        window.location.href = GOOGLE_MAPS_URL;
      }
    } catch {
      window.location.href = GOOGLE_MAPS_URL;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`offer-popup-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Tiwari Syndicate Grand Inauguration Offer"
    >
      <div
        className={`offer-popup-card ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Visible accessible Close Button */}
        <button
          type="button"
          className="offer-popup-close-btn"
          onClick={handleClose}
          aria-label="Close offer popup"
          title="Close (Esc)"
        >
          <svg
            className="offer-popup-close-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Complete Poster Image */}
        <div className="offer-popup-image-wrapper">
          <img
            src={imgSrc}
            alt="Tiwari Syndicate - Kalp Experience Centre Grand Inauguration Offer"
            className="offer-popup-image"
            onError={handleImageError}
            loading="eager"
            draggable="false"
          />
        </div>

        {/* Footer Area: Live Countdown + Reach Us Action Button */}
        <div className="offer-popup-footer">
          {/* Countdown timer component */}
          <div className="offer-popup-countdown-section">
            <div className="offer-popup-countdown-header">
              <span className="countdown-live-badge">
                <span className="countdown-live-dot" />
                {timeLeft.isComplete ? 'EVENT IS LIVE' : 'INAUGURATION COUNTDOWN'}
              </span>
              <span className="countdown-target-text">06 Nov 2026 • 11:00 AM</span>
            </div>

            {timeLeft.isComplete ? (
              <div className="countdown-completed-banner">
                🎉 Grand Inauguration is Live! Welcome to Kalp Experience Centre
              </div>
            ) : (
              <div className="countdown-timer-grid">
                <div className="countdown-unit-box">
                  <span className="countdown-val">{padZero(timeLeft.days)}</span>
                  <span className="countdown-lbl">DAYS</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-unit-box">
                  <span className="countdown-val">{padZero(timeLeft.hours)}</span>
                  <span className="countdown-lbl">HOURS</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-unit-box">
                  <span className="countdown-val">{padZero(timeLeft.minutes)}</span>
                  <span className="countdown-lbl">MINS</span>
                </div>
                <span className="countdown-separator">:</span>
                <div className="countdown-unit-box">
                  <span className="countdown-val">{padZero(timeLeft.seconds)}</span>
                  <span className="countdown-lbl">SECS</span>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Next Line: Reach Us Button linking to Google Maps */}
          <div className="offer-popup-reach-wrapper">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="offer-popup-reach-btn"
              onClick={handleReachUs}
              title="Open Tiwari Syndicate location in Google Maps"
            >
              <svg
                className="offer-popup-reach-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z" />
              </svg>
              <span>Reach Us</span>
              <svg
                className="offer-popup-reach-arrow"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
